/**
 * Centered section head for below-fold marketing sections.
 *
 * What: Renders an optional eyebrow, an h2, an optional body line, and an
 * optional trailing slot, centered with a 680px measure.
 * Why: The approved full-page design centers every below-fold section head at
 * a tighter type scale than `LandingSectionHeading` (which the school surfaces
 * still use left-aligned). Level pages share it, and their syllabus,
 * challenges and how-we-help sections carry no eyebrow copy, so the eyebrow
 * is optional rather than invented per section.
 * When: Update when the marketing below-fold heading rhythm changes.
 */
import type { ReactNode } from 'react';
import { Stack, Typography } from '@mui/material';

import { colors } from '@/components/landing/landingTokens';

interface LandingCenteredSectionHeadProps {
  titleId: string;
  title: string;
  eyebrow?: string;
  body?: string;
  /** Centered slot below the body — e.g. the program-facts review pill. */
  trailing?: ReactNode;
}

function LandingCenteredSectionHead({
  titleId,
  title,
  eyebrow,
  body,
  trailing,
}: LandingCenteredSectionHeadProps) {
  return (
    <Stack
      spacing={1.25}
      sx={{
        maxWidth: 680,
        mx: 'auto',
        textAlign: 'center',
        mb: { xs: 3, sm: 3.5 },
      }}
    >
      {eyebrow && (
        <Typography
          component="p"
          sx={{
            textTransform: 'uppercase',
            letterSpacing: '0.09em',
            fontWeight: 800,
            fontSize: 11,
            color: colors.accentCobalt,
          }}
        >
          {eyebrow}
        </Typography>
      )}
      <Typography
        id={titleId}
        component="h2"
        sx={{
          fontWeight: 800,
          fontSize: { xs: 24, sm: 28 },
          letterSpacing: '-0.02em',
          lineHeight: 1.15,
          color: colors.ink,
        }}
      >
        {title}
      </Typography>
      {body && (
        <Typography
          sx={{
            fontSize: 15,
            lineHeight: 1.6,
            color: colors.inkMuted,
            mt: 0.5,
          }}
        >
          {body}
        </Typography>
      )}
      {trailing}
    </Stack>
  );
}

export default LandingCenteredSectionHead;
